import { useCallback, useState } from 'react';
import { Check, Loader2, SearchCheck, Unlink } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

import { documentCaptureService } from '../../services/documentCaptureService';
import { useReviewDecision } from '../../hooks/useReviewDecision';
import type { ReviewQueueItem } from '../../types/documentCapture.types';

/**
 * Spot check — `AutoLinkAudit` (reason 23), 86d3x48q7 T13.
 *
 * The odd one out in this queue: **the scan is already linked.** Both readers agreed, so it linked
 * with nobody looking, and a random share of those agreements is sampled back to a person.
 *
 * ## Why this exists at all
 *
 * When the two readers agree, nothing downstream ever contradicts them — the document is filed and
 * the case is closed. So the residual failure mode, *both readers agree and both are wrong*, is
 * invisible by construction: there is no signal anywhere in the system that would reveal it. This
 * sample is the only thing that turns auto-link accuracy into a measured number rather than an
 * assumption, which is why the settings copy warns that a 0% rate leaves it unmeasured.
 *
 * ## Why it does not reuse the assign form
 *
 * There is nothing to type. The question is not "which document is this?" but "is the document it
 * already went to the right one?" — a yes/no against the paper. Reusing the number field would
 * invite the reviewer to re-derive an answer that already exists, and would make "confirm" look
 * like a re-link when it changes nothing at all.
 *
 * ## 86d3z2nzb — and it shows no identification facts either (owner decision, 2026-08-07)
 *
 * This panel used to render `CorroborationFacts` under "Linked to {docNumber}". Those facts are a
 * snapshot of DOP's own filed list taken BEFORE the scan was attached, so on a brand-new document
 * they printed *"Not in the registry — no such document exists"* directly beneath the number the
 * scan is already linked to: a flat self-contradiction on one screen, live on all 22 spot-check
 * items in production. They also answer a different question from the one this panel asks. Removed
 * rather than reworded — every other render site keeps them, corrected.
 */
interface SpotCheckReviewPanelProps {
  item: ReviewQueueItem;
}

export function SpotCheckReviewPanel({ item }: SpotCheckReviewPanelProps) {
  const { submit, submitting } = useReviewDecision();
  const [denying, setDenying] = useState(false);
  const [reason, setReason] = useState('');

  const confirmMatch = useCallback(() => {
    void submit(() => documentCaptureService.confirmSpotCheck(item.id, item.linkEpoch ?? 0), {
      successMessage: `${item.docNumber ?? 'Scan'} confirmed — it matches the paper`,
    });
  }, [submit, item.id, item.linkEpoch, item.docNumber]);

  const denyMatch = useCallback(() => {
    if (!reason.trim()) return;
    // The existing admin unlink flow: clears the custbody in NetSuite and marks the document
    // withdrawn. Needs docscan.manage — confirming does not, because confirming undoes nothing.
    void submit(() => documentCaptureService.reject(item.id, reason.trim(), item.linkEpoch ?? 0, true), {
      successMessage: 'Unlinked — the scan is back for re-filing',
    });
  }, [submit, item.id, reason, item.linkEpoch]);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-info/30 bg-info/5 p-3">
        <div className="flex items-start gap-2">
          <SearchCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-info" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">Spot check — this scan linked automatically</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Both readers agreed and nobody looked. Confirm the paper matches, or unlink.
            </p>
          </div>
        </div>
      </div>

      <div>
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Linked to
        </span>
        <div className="mt-0.5 font-mono text-[19px] font-bold leading-tight text-foreground">
          {item.docNumber ?? '—'}
        </div>
        {item.entityName && <p className="mt-0.5 text-xs text-muted-foreground">{item.entityName}</p>}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={confirmMatch} disabled={submitting}>
          {submitting ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Check className="mr-1.5 h-4 w-4" aria-hidden />
          )}
          Matches — confirm
        </Button>
        <Button variant="ghost" size="sm" disabled={submitting} onClick={() => setDenying((v) => !v)}>
          <Unlink className="mr-1.5 h-4 w-4" aria-hidden />
          Doesn’t match — unlink &amp; refile
        </Button>
      </div>

      {denying && (
        <div className="space-y-2 rounded-lg border border-destructive/25 bg-destructive/5 p-3">
          <label htmlFor="spotcheck-reason" className="block text-sm font-medium text-foreground">
            What’s on the paper instead? <span className="text-destructive">*</span>
          </label>
          <Textarea
            id="spotcheck-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. The paper is DR-000044626, not this document."
            rows={2}
          />
          <p className="text-xs text-muted-foreground">
            This clears the link in NetSuite and returns the scan for re-filing.
          </p>
          <Button variant="destructive" size="sm" disabled={!reason.trim() || submitting} onClick={denyMatch}>
            Unlink &amp; refile
          </Button>
        </div>
      )}
    </div>
  );
}
